/**
 * Angebotssumme nachtragen.
 *
 * Gebraucht wird das nur für die vor dem Go-live aus Monday übernommenen
 * Vorgänge. Alles, was seither in Kora versendet wurde, hat die Summe von
 * Anfang an – sie ist beim Wechsel auf „Gesendet" Pflicht.
 *
 * Eine 0 wird abgewiesen: Sie verfälscht die Kopfsumme still, während ein
 * leeres Feld ehrlich sagt, dass die Zahl fehlt.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { betragFormatieren, betragLesen, eur } from '../hilfen/format';
import type { Angebot } from '../typen';

export function SummeModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [text, setText] = useState(betragFormatieren(String(angebot.summe ?? '')));
  const [fehler, setFehler] = useState('');

  const speichern = useMutation({
    mutationFn: (summe: number) => api.aendere(`/angebote/${angebot.id}/summe`, { summe }),
    onSuccess: (_daten, summe) => {
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
      toastZeigen(`Angebotssumme ${eur(summe)} eingetragen.`);
      onFertig();
      onSchliessen();
    },
    onError: (f) =>
      setFehler(f instanceof ApiFehler ? f.message : 'Die Summe konnte nicht gespeichert werden.'),
  });

  function absenden() {
    setFehler('');
    const wert = betragLesen(text);
    if (!wert || wert <= 0) {
      setFehler('Bitte einen Betrag größer als null angeben – geschätzt wird hier nicht.');
      return;
    }
    speichern.mutate(wert);
  }

  return (
    <Modal
      titel="Angebotssumme nachtragen"
      untertitel={angebot.name}
      breite={440}
      fest={speichern.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={speichern.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={absenden} disabled={speichern.isPending}>
            {speichern.isPending ? 'Wird gespeichert …' : 'Übernehmen'}
          </KnopfPrimaer>
        </>
      }
    >
      <label className="modal-feld">
        <span>Angebotssumme netto</span>
        <input
          className="modal-eingabe"
          value={text}
          autoFocus
          inputMode="decimal"
          placeholder="z. B. 42.800"
          onChange={(e) => setText(betragFormatieren(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') absenden();
          }}
        />
      </label>

      <p className="eh-hinweis">
        Dieser Vorgang stammt aus dem Monday-Import. Mit der Summe zählt er in „Auf deinem Tisch"
        mit – bis dahin nennt die Kopfzeile ihn ausdrücklich als fehlend.
      </p>

      {fehler && <div className="eh-fehler">{fehler}</div>}
    </Modal>
  );
}